import React, { useMemo, useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import useViewportChangeTick from "../../../hooks/useViewportChangeTick";
import { VISIBILITY_OPTIONS, findVisibilityOption } from "./options";
import "./index.scss";

const MENU_GAP = 8;
const VIEWPORT_PADDING = 12;

function PostVisibilityControl({ value, onChange, disabled, size, className, onOpenChange, options }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });

    const resolvedOptions = useMemo(
        () => (Array.isArray(options) && options.length > 0 ? options : VISIBILITY_OPTIONS),
        [options],
    );
    const activeOption = useMemo(() => findVisibilityOption(value, resolvedOptions), [value, resolvedOptions]);
    const activeOptionValue = activeOption?.value;

    const updateOpen = useCallback(
        (nextOpen) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [onOpenChange]
    );

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event) => {
            const insideTrigger = containerRef.current?.contains(event.target);
            const insideMenu = menuRef.current?.contains(event.target);
            if (!insideTrigger && !insideMenu) {
                updateOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                updateOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [open, updateOpen]);

    useEffect(() => () => onOpenChange?.(false), [onOpenChange]);

    const handleToggle = () => {
        if (disabled) return;
        updateOpen(!open);
    };

    const handleSelect = (nextValue) => {
        if (disabled) return;
        if (nextValue === activeOption.value) {
            updateOpen(false);
            return;
        }
        onChange?.(nextValue);
        updateOpen(false);
    };

    const resolveScrollContainers = useCallback(() => {
        // Watch any scrollable ancestor so the portal-rendered menu re-anchors
        // when the page scrolls underneath the trigger.
        const ancestors = typeof window !== "undefined" ? [window] : [];
        let node = containerRef.current?.parentElement || null;
        while (node && node !== document.body) {
            const style = window.getComputedStyle(node);
            const overflowY = style.overflowY;
            if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
                ancestors.push(node);
            }
            node = node.parentElement;
        }
        return ancestors;
    }, []);

    const viewportTick = useViewportChangeTick(resolveScrollContainers, [open]);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        const dropdown = menuRef.current;
        const trigger = triggerRef.current;
        if (!dropdown || !trigger) {
            return;
        }

        // Position in viewport (fixed) coordinates so the portal-rendered menu
        // can escape any ancestor `overflow: hidden` / `transform` containers
        // (e.g. the editorial post card).
        const triggerRect = trigger.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = triggerRect.bottom + MENU_GAP;
        let left = triggerRect.left;

        // Flip vertically if there's not enough space below.
        if (top + dropdownRect.height + VIEWPORT_PADDING > viewportHeight) {
            const flippedTop = triggerRect.top - MENU_GAP - dropdownRect.height;
            if (flippedTop >= VIEWPORT_PADDING) {
                top = flippedTop;
            } else {
                top = Math.max(VIEWPORT_PADDING, viewportHeight - dropdownRect.height - VIEWPORT_PADDING);
            }
        }

        // Clamp horizontally to viewport.
        const maxLeft = viewportWidth - dropdownRect.width - VIEWPORT_PADDING;
        if (left > maxLeft) left = maxLeft;
        if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;

        setMenuCoords({ top, left });
    }, [open, viewportTick, activeOptionValue, size]);

    return (
        <div
            ref={containerRef}
            className={classNames(
                "post-visibility-control",
                `post-visibility-control--${size}`,
                {
                    "is-open": open,
                    "is-disabled": disabled,
                },
                className,
            )}
        >
            <button
                type="button"
                className="post-visibility-control__trigger"
                onClick={handleToggle}
                ref={triggerRef}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <span className="post-visibility-control__icon" aria-hidden="true">
                    <activeOption.Icon />
                </span>
                <span className="post-visibility-control__label">{t(activeOption.labelKey)}</span>
            </button>
            {open && (
                (() => {
                    const portalRoot = typeof document !== 'undefined'
                        ? document.getElementById('portal') || document.body
                        : null;
                    const menuNode = (
                        <div
                            className="post-visibility-control__menu"
                            role="menu"
                            ref={menuRef}
                            style={{ top: menuCoords.top, left: menuCoords.left, right: "auto" }}
                        >
                            {resolvedOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={option.value === activeOption.value}
                                    className={classNames("post-visibility-control__menu-item", {
                                        "is-active": option.value === activeOption.value,
                                    })}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    <span className="post-visibility-control__menu-icon" aria-hidden="true">
                                        <option.Icon />
                                    </span>
                                    <span className="post-visibility-control__menu-text">
                                        <span className="post-visibility-control__menu-label">{t(option.labelKey)}</span>
                                        <span className="post-visibility-control__menu-description">{t(option.descriptionKey)}</span>
                                    </span>
                                    {option.value === activeOption.value && (
                                        <span className="post-visibility-control__check" aria-hidden="true">✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    );
                    return portalRoot ? ReactDOM.createPortal(menuNode, portalRoot) : menuNode;
                })()
            )}
        </div>
    );
}

PostVisibilityControl.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(["sm", "md"]),
    className: PropTypes.string,
    onOpenChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        labelKey: PropTypes.string.isRequired,
        descriptionKey: PropTypes.string.isRequired,
        Icon: PropTypes.elementType.isRequired,
    })),
};

PostVisibilityControl.defaultProps = {
    value: "Public",
    onChange: undefined,
    disabled: false,
    size: "md",
    className: "",
    onOpenChange: undefined,
    options: undefined,
};

export default PostVisibilityControl;
